import type { TFunction } from "i18next"
import { RotateCcwIcon } from "lucide-react"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { PaymentMethodNameEnum } from "@/enums"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

import { getTransactionStatusLabel } from "./transaction-status"

type RefundTransaction = {
  id: string
  amount: number
  status: string
  processorInformation?: {
    name?: string
    tid?: string
    nsu?: string
    message?: string
    returnCode?: string
  }
  createdAt: string
  updatedAt: string
}

interface RefundInfoProps {
  refundedTransactions?: RefundTransaction[]
  reversedTransactions?: RefundTransaction[]
  paymentMethod?: string
  t: TFunction
}

const getRefundStatusVariant = (status?: string) => {
  switch (status?.toUpperCase()) {
    case "COMPLETED":
    case "SUCCESS":
      return "default"
    case "PENDING":
    case "PROCESSING":
      return "secondary"
    case "CANCELLED":
    case "FAILED":
      return "destructive"
    default:
      return "outline"
  }
}

export const RefundInfo = ({
  refundedTransactions,
  reversedTransactions,
  paymentMethod,
  t,
}: RefundInfoProps) => {
  // Valores de estorno/reembolso só são exibidos para cartão de crédito.
  if (paymentMethod !== PaymentMethodNameEnum.CREDIT_CARD) {
    return null
  }

  const translationKey = "orders.transactionsTable.refunds"
  const allRefunds = [
    ...(refundedTransactions || []),
    ...(reversedTransactions || []),
  ]

  if (allRefunds.length === 0) {
    return null
  }

  const totalRefunded = allRefunds.reduce(
    (sum, refund) => sum + refund.amount,
    0
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 border-orange-300 bg-orange-50 text-xs text-orange-700 hover:bg-orange-100"
        >
          <RotateCcwIcon size={12} />
          {allRefunds.length === 1
            ? t(`${translationKey}.single`)
            : t(`${translationKey}.multiple`, {
                count: allRefunds.length,
              })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="border-b bg-muted/50 px-4 py-3">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <RotateCcwIcon size={14} />
              {t(`${translationKey}.title`)}
            </h4>
            <Badge variant="secondary">
              {t(`${translationKey}.total`)}:{" "}
              {formatMoney(totalRefunded / 100)}
            </Badge>
          </div>
        </div>
        <div className="max-h-[300px] overflow-auto">
          {allRefunds.map((refund, index) => (
            <div
              key={refund.id}
              className={`space-y-2 p-4 ${
                index < allRefunds.length - 1
                  ? "border-b"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {refund.id}
                </code>
                <Badge
                  variant={getRefundStatusVariant(
                    refund.status
                  )}
                >
                  {getTransactionStatusLabel(
                    refund.status,
                    t
                  )}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(`${translationKey}.amount`)}
                  </span>
                  <span className="font-semibold text-orange-600">
                    -{formatMoney(refund.amount / 100)}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(`${translationKey}.date`)}
                  </span>
                  <span>
                    {DateHelper.format(
                      refund.createdAt,
                      "dd/MM/yyyy HH:mm"
                    )}
                  </span>
                </div>
              </div>
              {refund.processorInformation &&
                (refund.processorInformation.name ||
                  refund.processorInformation.tid ||
                  refund.processorInformation.nsu ||
                  refund.processorInformation.message ||
                  refund.processorInformation
                    .returnCode) && (
                  <div className="rounded-md border bg-muted/30 p-2">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      {t(
                        "orders.transactionsTable.processorInfo.title"
                      )}
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {refund.processorInformation.name && (
                        <div>
                          <span className="text-muted-foreground">
                            {t(
                              "orders.transactionsTable.processorInfo.processor"
                            )}
                            :{" "}
                          </span>
                          {refund.processorInformation.name}
                        </div>
                      )}
                      {refund.processorInformation.tid && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-muted-foreground">
                            TID
                          </span>
                          <div className="flex items-center gap-1">
                            <code className="max-w-[100px] truncate rounded bg-muted px-1.5 py-0.5 text-xs">
                              {
                                refund.processorInformation
                                  .tid
                              }
                            </code>
                            <ClipboardButton
                              text={
                                refund.processorInformation
                                  .tid
                              }
                              variant="ghost"
                              className="h-4 w-4 shrink-0"
                            />
                          </div>
                        </div>
                      )}
                      {refund.processorInformation.nsu && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-muted-foreground">
                            NSU
                          </span>
                          <div className="flex items-center gap-1">
                            <code className="max-w-[100px] truncate rounded bg-muted px-1.5 py-0.5 text-xs">
                              {
                                refund.processorInformation
                                  .nsu
                              }
                            </code>
                            <ClipboardButton
                              text={
                                refund.processorInformation
                                  .nsu
                              }
                              variant="ghost"
                              className="h-4 w-4 shrink-0"
                            />
                          </div>
                        </div>
                      )}
                      {refund.processorInformation
                        .returnCode && (
                        <div>
                          <span className="text-muted-foreground">
                            {t(
                              "orders.transactionsTable.processorInfo.returnCode"
                            )}
                            :{" "}
                          </span>
                          {
                            refund.processorInformation
                              .returnCode
                          }
                        </div>
                      )}
                    </div>
                    {refund.processorInformation
                      .message && (
                      <div className="mt-1 text-xs">
                        <span className="text-muted-foreground">
                          {t(
                            "orders.transactionsTable.processorInfo.message"
                          )}
                          :{" "}
                        </span>
                        {
                          refund.processorInformation
                            .message
                        }
                      </div>
                    )}
                  </div>
                )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
